<script setup lang="ts">
import { ref, computed, toRefs } from "vue";
import { useId } from "vue";
import {
  arrow,
  useFloating,
  flip,
  autoUpdate,
  shift,
  offset,
  type Placement,
} from "@floating-ui/vue";
import { useHoverBridgePosition } from "./useHoverBridgePosition.composable";
import { useInteractions } from "./useInteractions.composable";

const props = withDefaults(
  defineProps<{
    title?: string;
    text: string;
    isToggletip?: boolean;
    placement?: Placement;
  }>(),
  {
    isToggletip: false,
    placement: "bottom",
  },
);

const { isToggletip, placement: preferredPlacement } = toRefs(props);

const OFFSET_VALUE = 16;

const slots = defineSlots<{
  default?: void;
  footer?: void;
}>();

const visible = ref(false);

const anchor = ref<HTMLElement>();
const tooltip = ref<HTMLElement>();
const tooltipArrow = ref<HTMLElement>();

const {
  floatingStyles,
  middlewareData,
  placement: currentPlacement,
} = useFloating(anchor, tooltip, {
  placement: preferredPlacement,
  middleware: [
    shift(),
    flip(),
    offset(OFFSET_VALUE),
    arrow({ element: tooltipArrow }),
  ],
  whileElementsMounted(...args) {
    const cleanup = autoUpdate(...args, { animationFrame: true });
    return cleanup;
  },
});

const OPPOSITE_SIDE: { [key in string]: string } = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right",
};

const oppositeSide = computed(() => OPPOSITE_SIDE[currentPlacement.value]);

const arrowStyles = computed(() => ({
  position: "absolute" as const,
  left:
    middlewareData.value.arrow?.x != null
      ? `${middlewareData.value.arrow.x}px`
      : "",
  top:
    middlewareData.value.arrow?.y != null
      ? `${middlewareData.value.arrow.y}px`
      : "",
  right: "",
  bottom: "",
  // @ts-expect-error: TODO: This new error showed up after updating @vue/tsconfig. Examine if this is really an issue (or `?` or `!` can be applied easily)
  [oppositeSide.value]: `-6px`,
  transform: "rotate(45deg)",
}));

const id = useId();

const { hoverBridgeStyles } = useHoverBridgePosition({
  anchor,
  tooltip,
  middlewareData,
  placement: currentPlacement,
  visible,
  offset: OFFSET_VALUE,
});

const {
  handleMouseOver,
  handleMouseOut,
  handleClick,
  handleAnchorKeydown,
  handleTooltipKeydown,
} = useInteractions({
  anchor,
  tooltip,
  tooltipArrow,
  visible,
  isToggletip,
});
</script>

<template>
  <!--
    The slot is needed to make the tooltip component customizable and reusable with different anchors.
    Unfortunately, there is no way to attach the listeners and refs to the slot, so we use a wrapping div.
    This still works and is accessible if the slot element is providing a label, e.g. via `<Button caption="…" />`.
  -->
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
  <div
    ref="anchor"
    class="tooltip--anchor"
    data-testid="tooltip"
    @mouseover="handleMouseOver"
    @mouseout="handleMouseOut"
    @focusin="handleMouseOver"
    @focusout="handleMouseOut"
    @click="handleClick"
    @keydown="handleAnchorKeydown"
  >
    <slot></slot>
  </div>

  <Teleport
    v-if="visible"
    to="body"
  >
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/mouse-events-have-key-events -->
    <div
      :id="id"
      ref="tooltip"
      class="tooltip"
      role="tooltip"
      :aria-live="visible ? 'polite' : 'off'"
      :style="floatingStyles"
      @mouseout="handleMouseOut"
      @keydown="handleTooltipKeydown"
    >
      <header
        v-if="title"
        class="tooltip--title"
      >
        {{ title }}
      </header>
      <div class="tooltip--text">{{ text }}</div>
      <div
        v-if="slots.footer"
        class="tooltip--footer"
      >
        <slot name="footer"></slot>
      </div>
      <span
        ref="tooltipArrow"
        class="tooltip--arrow"
        :style="arrowStyles"
      ></span>
      <span
        class="tooltip--hover-bridge"
        :style="hoverBridgeStyles"
      ></span>
    </div>
  </Teleport>
</template>

<style scoped>
@import "Tooltip.css";
</style>
